import { type BrowserConfig, type EnrichmentPlugin, type Event, PluginType } from "@amplitude/analytics-types";

export interface DeviceIdentificationPluginConfig {
  deviceId: string | null;
  platform: string | null;
  version: string | null;
}

/**
 * Amplitude Plugin to allow overriding or specifying the user's device/app identification.
 *
 * This is to backfill dropped support from the migration of amplitude-js -> @amplitude/browser-anajytics
 * of the following config options:
 * - `config.deviceIdFromUrlParam`
 * - `config,platform`
 * - `config.version`
 *
 * And the following client methods:
 * - `setVersionName()`
 * - `setDeviceId()`
 *
 * @see https://www.docs.developers.amplitude.com/data/sdks/typescript-browser/migration/
 */
export class DeviceIdentificationPlugin implements EnrichmentPlugin {
  name = "@rewardStyle/device-identification";
  type = PluginType.ENRICHMENT as const;

  config!: BrowserConfig;

  device_id: string | null;
  platform: string | null;
  version: string | null;

  constructor({ deviceId, platform, version }: DeviceIdentificationPluginConfig) {
    this.device_id = deviceId;
    this.platform = platform;
    this.version = version;
  }

  /**
   * setup() is called on plugin installation
   * example: client.add(new LibraryModifierPlugin());
   */
  setup(config: BrowserConfig): Promise<void> {
    this.config = config;

    return Promise.resolve();
  }

  execute(event: Event): Promise<Event> {
    if (this.device_id !== null) {
      event.device_id = this.device_id;
    }
    if (this.platform !== null) {
      event.platform = this.platform;
    }
    if (this.version !== null) {
      event.version_name = this.version;
    }

    return Promise.resolve(event);
  }
}
